/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Rating,
} from '@mui/material';
import {
  Login as LoginIcon
} from '@mui/icons-material';
import { useDebate } from '../contexts/DebateContext';
import { useAuth } from '../contexts/AuthContext';
import { Message, Team } from '../types';
import { joinTeam } from '../services/debateService';
import { toast } from 'react-hot-toast';
import { formatMessageContent } from '../utils/messageFormatter';
import logger from '../utils/logger';

// Simplified version of DebateRoomPage for view-only mode
const DebateViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentDebate, fetchDebateById, loading, error } = useDebate();
  const { isAuthenticated } = useAuth();
  const [teamColors, setTeamColors] = useState<{[key: string]: string}>({});
  
  // State for team selection dialog
  const [teamSelectOpen, setTeamSelectOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  
  // Check for join=true in the URL query parameters
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const shouldOpenJoinDialog = searchParams.get('join') === 'true';
  
  // Ref for scrolling to bottom
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Fetch debate data only once on component mount
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (id && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchDebateById(id).catch(err => {
        logger.error('Error fetching debate:', err);
      });
    }
  }, [id, fetchDebateById]);

  // Use WebSocket for debate updates instead of polling
  useEffect(() => {
    // This function is intentionally left blank - we no longer need polling
    // Updates will come through the WebSocket connection in DebateContext
    logger.info('DebateViewerPage: Using WebSocket for live updates instead of polling');
  }, []);
  
  // Set team colors
  useEffect(() => {
    if (currentDebate?.teams && currentDebate.teams.length > 0) {
      const colors: {[key: string]: string} = {};
      
      // Set colors for each team
      currentDebate.teams.forEach((team, index) => {
        colors[team.id] = index === 0 ? '#e3f2fd' : '#fce4ec'; // Blue for team A, Pink for team B
      });
      
      setTeamColors(colors);
    }
  }, [currentDebate?.teams]);
  
  // Scroll to bottom when new messages come in
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentDebate?.messages]);
  
  // Automatically open team selection dialog if join=true is in the URL
  useEffect(() => {
    if (shouldOpenJoinDialog && currentDebate && isAuthenticated && currentDebate.publicJoinable) {
      // Only open if the debate is loaded and user is authenticated
      setTeamSelectOpen(true);
      
      // Clear the join parameter from the URL to avoid reopening on refresh
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [shouldOpenJoinDialog, currentDebate, isAuthenticated]);
  
  // Render a message bubble
  const renderMessage = (message: Message, index: number) => {
    // Find the team this message belongs to
    const team = currentDebate?.teams?.find(t => t.id === message.teamId);
    const teamName = team?.name || 'Unknown Team';
    const backgroundColor = teamColors[message.teamId] || '#f5f5f5';
    
    // Format timestamp
    const timestamp = message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : '';
    
    // Check if message has any flags
    const hasFlags = message.flags && message.flags.length > 0;
    const unliftedFlagsCount = message.flags?.filter(f => !f.lifted).length || 0;
    
    // Determine if this is the first team (left side) or second team (right side)
    const isFirstTeam = currentDebate?.teams?.[0]?.id === message.teamId;
    
    // Style based on team position and flags
    const messageStyle = {
      my: 1,
      p: 2,
      borderRadius: 2,
      maxWidth: '80%',
      position: 'relative',
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      ml: isFirstTeam ? null : 'auto',
      mr: isFirstTeam ? 'auto' : null,
      bgcolor: unliftedFlagsCount > 0 ? '#f3e5f5' : backgroundColor,
      borderLeft: unliftedFlagsCount > 0 ? '4px solid #9c27b0' : 'none'
    };
    
    return (
      <Box 
        key={message.id || index}
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          mb: 2
        }}
      >
        {/* Team name */}
        <Typography
          variant="caption"
          sx={{
            ml: isFirstTeam ? 2 : 'auto',
            mr: isFirstTeam ? 'auto' : 2,
            fontWeight: 'bold',
            color: isFirstTeam ? 'primary.main' : 'secondary.main',
            mb: 0.5
          }}
        >
          {teamName}
        </Typography>
        
        {/* Message content */}
        <Paper sx={messageStyle}>
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word'
            }}
            dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content, { unverifiedLinkNote: t('unverifiedLinkNote') }) }}
          />
          
          {/* Display flags if any */}
          {hasFlags && (
            <Box sx={{ mt: 2 }}>
              {message.flags.map((flag, flagIndex) => (
                <Chip 
                  key={flagIndex}
                  label={flag.type}
                  color="error"
                  variant={flag.lifted ? "outlined" : "filled"}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
          
          {/* Timestamp */}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              textAlign: isFirstTeam ? 'left' : 'right',
              color: 'text.secondary'
            }}
          >
            {timestamp}
          </Typography>
        </Paper>
      </Box>
    );
  };
  
  // Open team selection dialog
  const handleOpenTeamSelect = () => {
    if (!isAuthenticated) {
      toast.error('Please log in to join a team');
      return;
    }
    setTeamSelectOpen(true);
  };
  
  // Close team selection dialog
  const handleCloseTeamSelect = () => {
    setTeamSelectOpen(false);
  };
  
  // Handle team selection and join
  const handleTeamSelect = async () => {
    if (!selectedTeamId) {
      toast.error('Please select a team to join');
      return;
    }
    
    try {
      await joinTeam(id!, selectedTeamId);
      toast.success('Successfully joined team!');
      // Force a hard reload into the debate room to ensure fresh state
      window.location.href = `/debate/${id}`;
    } catch (error) {
      logger.error('Error joining team:', error);
      toast.error('Failed to join team');
    } finally {
      setTeamSelectOpen(false);
    }
  };
  
  // Check if a team has available spots
  const hasAvailableSpots = (team: Team): boolean => {
    // Get team configuration
    const maxMembers = team.maxMembers || 1;
    const publicSpots = team.publicSpots || 0;
    const currentMembers = team.members?.length || 0;
    
    // Check if the team is full
    if (currentMembers >= maxMembers) {
      return false;
    }
    
    // If the debate is publicly joinable, check if there are public spots available
    if (currentDebate?.publicJoinable) {
      // Check if this is the creator's team (first team)
      const isCreatorTeam = currentDebate.teams[0]?.id === team.id;
      
      // For creator's team, we need to exclude the creator from the count
      let effectiveMembers = currentMembers;
      if (isCreatorTeam && effectiveMembers > 0) {
        // Subtract 1 to account for the creator (who is already in the team)
        effectiveMembers -= 1;
      }
      
      return publicSpots > 0 && effectiveMembers < publicSpots;
    }
    
    // If we get here, the user needs an invite to join
    return false;
  };
  
  // Add helper text to explain why a team is not joinable
  const getTeamJoinabilityMessage = (team: Team): string => {
    const maxMembers = team.maxMembers || 1;
    const publicSpots = team.publicSpots || 0;
    const currentMembers = team.members?.length || 0;
    
    if (currentMembers >= maxMembers) {
      return 'This team is full';
    }
    
    if (currentDebate?.publicJoinable) {
      if (publicSpots === 0) {
        return 'This team requires an invitation to join';
      }
      
      // Check if this is the creator's team (first team)
      const isCreatorTeam = currentDebate.teams[0]?.id === team.id;
      
      // For creator's team, we need to exclude the creator from the count
      let effectiveMembers = currentMembers;
      if (isCreatorTeam && effectiveMembers > 0) {
        // Subtract 1 to account for the creator (who is already in the team)
        effectiveMembers -= 1;
      }
      
      if (effectiveMembers >= publicSpots) {
        return 'All public spots are filled';
      }
      return `${publicSpots - effectiveMembers} public spots available`;
    }
    
    return 'Requires invitation to join';
  };
  
  // Replace the old handleJoinDebate with the new one that opens the team selection dialog
  const handleJoinDebate = () => {
    handleOpenTeamSelect();
  };
  
  const formatWinnerDeterminationReason = (reason: string): string => {
    switch(reason) {
      case 'user_concession':
        return 'User concession';
      case 'gauntlet_limit':
        return 'Exceeded Jester\'s Gauntlet limit';
      case 'auto_completed_inactive':
        return 'Auto-completed due to 7 days of inactivity';
      case 'manual':
        return 'Manual determination';
      default:
        return reason;
    }
  };

  const formatCubeDescription = (description: string, isCubeChallenge: boolean): string => {
    if (!isCubeChallenge) {
      return description;
    }

    // Check if it's the old format that needs reformatting
    const oldFormatPattern = /^(AI )?Defense of explanation: "(.*)" against challenge: "(.*)"$/;
    const match = description.match(oldFormatPattern);
    
    if (match) {
      const [, , explanation, challenge] = match;
      return `<strong>EXPLANATION BEING DEFENDED:</strong><br/>"${explanation}"<br/><br/><strong>CHALLENGE:</strong><br/>"${challenge}"`;
    }
    
    // If it's already in new format with **, convert to HTML
    if (description.includes('**EXPLANATION BEING DEFENDED:**')) {
      return description
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br/>');
    }
    
    // If it's neither format, return as-is
    return description;
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }
  
  if (!currentDebate) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          Debate not found or not accessible.
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Debate Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {currentDebate.topic}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Chip 
              label={currentDebate.status.toUpperCase()} 
              color={currentDebate.status === 'active' ? 'success' : 'default'}
              sx={{ mr: 1 }}
            />
            <Typography variant="subtitle1" color="text.secondary" component="span">
              Created: {new Date(currentDebate.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          
          {isAuthenticated && currentDebate.status === 'active' && currentDebate.publicJoinable && (
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<LoginIcon />}
              onClick={handleJoinDebate}
            >
              Join Debate
            </Button>
          )}
          
          {isAuthenticated && currentDebate.status === 'active' && !currentDebate.publicJoinable && (
            <Chip 
              label="View Only" 
              color="secondary"
              variant="outlined"
              sx={{ fontWeight: 'medium' }}
            />
          )}
        </Box>
        
        {currentDebate.description && (
          <Box sx={{ mt: 2 }}>
            {currentDebate.cubeChallenge?.isCubeChallenge ? (
              <Typography 
                variant="body1" 
                dangerouslySetInnerHTML={{ 
                  __html: formatCubeDescription(currentDebate.description, true) 
                }} 
              />
            ) : (
              <Typography variant="body1">
                {currentDebate.description}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Teams Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {currentDebate.teams.map((team, index) => (
          <Grid item xs={12} md={6} key={team.id}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                bgcolor: teamColors[team.id] || '#f5f5f5',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="h6" gutterBottom>
                {team.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {team.viewpoint}
              </Typography>
              
              {/* Show team members if any */}
              {team.members && team.members.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Team Members: {team.members.length}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      {/* Messages */}
      <Paper elevation={3} sx={{ p: 2, mb: 4, minHeight: '400px', maxHeight: '600px', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Debate Conversation
          </Typography>
          
          <Divider sx={{ mb: 3 }} />
          
          {currentDebate.messages && currentDebate.messages.length > 0 ? (
            <Box>
              {currentDebate.messages.map((message, index) => (
                renderMessage(message, index)
              ))}
              <div ref={messagesEndRef} />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No messages have been exchanged in this debate yet.
            </Alert>
          )}
        </Box>
      </Paper>
      
      {/* Team Selection Dialog */}
      <Dialog open={teamSelectOpen} onClose={handleCloseTeamSelect}>
        <DialogTitle>Select a Team to Join</DialogTitle>
        <DialogContent>
          {!currentDebate.publicJoinable ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              This debate is not publicly joinable. You need a direct invitation to join one of the teams.
            </Alert>
          ) : (
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <RadioGroup
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
              >
                {currentDebate?.teams.map((team) => (
                  <FormControlLabel
                    key={team.id}
                    value={team.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1">{team.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {team.viewpoint}
                        </Typography>
                        {team.members && (
                          <Typography variant="caption" display="block">
                            Current members: {team.members.length}
                            {team.maxMembers && ` / ${team.maxMembers}`}
                          </Typography>
                        )}
                        <Typography 
                          variant="caption" 
                          display="block"
                          color={hasAvailableSpots(team) ? "success.main" : "error.main"}
                        >
                          {getTeamJoinabilityMessage(team)}
                        </Typography>
                      </Box>
                    }
                    disabled={!hasAvailableSpots(team)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeamSelect}>
            Cancel
          </Button>
          <Button 
            onClick={handleTeamSelect} 
            variant="contained" 
            color="primary"
            disabled={!selectedTeamId || !currentDebate.publicJoinable}
          >
            Join Team
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Debate Summary if completed */}
      {currentDebate.status === 'completed' && currentDebate.summary && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {currentDebate.cubeChallenge?.isCubeChallenge ? 'Challenge Assessment' : 'Debate Summary'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ 
            p: 2, 
            bgcolor: currentDebate.cubeChallenge?.isCubeChallenge 
              ? (currentDebate.summary.includes('CHALLENGE SUCCESSFUL') ? 'success.light' : 'info.light')
              : 'transparent',
            borderRadius: 1,
            border: currentDebate.cubeChallenge?.isCubeChallenge ? '1px solid' : 'none',
            borderColor: currentDebate.cubeChallenge?.isCubeChallenge 
              ? (currentDebate.summary.includes('CHALLENGE SUCCESSFUL') ? 'success.main' : 'info.main')
              : 'transparent'
          }}>
            {currentDebate.summary.split('\n').map((paragraph: string, index: number) => {
              // Special formatting for CUBE challenge assessment
              if (currentDebate.cubeChallenge?.isCubeChallenge) {
                if (paragraph.includes('🎯 CHALLENGE SUCCESSFUL') || paragraph.includes('🛡️ EXPLANATION MAINTAINED')) {
                  return (
                    <Typography key={index} variant="h6" sx={{ 
                      fontWeight: 'bold', 
                      color: paragraph.includes('SUCCESSFUL') ? 'success.dark' : 'info.dark',
                      mb: 2
                    }}>
                      {paragraph}
                    </Typography>
                  );
                }
                if (paragraph.startsWith('CHALLENGE ASSESSMENT:') || paragraph.startsWith('FINAL DECISION:')) {
                  return (
                    <Typography key={index} variant="subtitle1" sx={{ 
                      fontWeight: 'bold', 
                      mt: 2, 
                      mb: 1,
                      color: 'primary.dark'
                    }}>
                      {paragraph}
                    </Typography>
                  );
                }
              }
              
              return (
                <Typography key={index} variant="body1" paragraph>
                  {paragraph}
                </Typography>
              );
            })}
          </Box>
          
          {currentDebate.winner && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Winner: {currentDebate.teams.find(t => t.id === currentDebate.winner)?.name || 'Unknown Team'}
              </Typography>
              {currentDebate.metrics?.winnerJustification && currentDebate.winnerDeterminationReason === 'auto_completed_inactive' && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Justification:</strong> {currentDebate.metrics.winnerJustification}
                </Typography>
              )}
              {currentDebate.winnerDeterminationReason && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Determination method: {formatWinnerDeterminationReason(currentDebate.winnerDeterminationReason)}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Show debate metrics */}
          {currentDebate.metrics && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Debate Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Overall Quality</Typography>
                    <Rating value={(currentDebate.metrics.overallQuality || 5) / 2} precision={0.5} readOnly />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Explanation Quality</Typography>
                    <Rating value={(currentDebate.metrics.explanationQuality || 5) / 2} precision={0.5} readOnly />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Civil Discourse</Typography>
                    <Rating value={(currentDebate.metrics.civilDiscourse || 5) / 2} precision={0.5} readOnly />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default DebateViewerPage; 